
// Libraries
import { Component, namespace, Vue } from "nuxt-property-decorator";

// Components
import dayjs from "dayjs";
import RouterLink from "~/components/atoms/text/RouterLink.vue";
import BlogPostAuthor from "~/components/molecules/display/BlogPostAuthor.vue";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import DailySvgChart from "~/components/molecules/display/DailySvgChart.vue";
import LoadingSpinner from "~/components/molecules/display/LoadingSpinner.vue";
import PressContactBox from "~/components/molecules/display/PressContactBox.vue";
import PriceTrendVote from "~/components/molecules/form/PriceTrendVote.vue";
import ShareNav from "~/components/organisms/nav/ShareNav.vue";

// Mixins
import { Jsonld } from "nuxt-jsonld";
import { returnAuthorDataByID } from "~/mixins/mixins";
import { generateMetaTags } from "~/operations/meta-helper-functions";

const BlogModule = namespace("blog");
const EsyoilModule = namespace("esyoil");
const BreadCrumbModule = namespace("breadcrumb");

@Jsonld
@Component({
  components: {
    RouterLink,
    BlogPostAuthor,
    DailySvgChart,
    PressContactBox,
    PriceTrendVote,
    LoadingSpinner,
    ShareNav,
    BreadCrumb,
  },
  methods: {
    returnAuthorDataByID,
  },
})
export default class SingleArticle extends Vue {
  jsonld() {
    const breadCrumbsArray = this.breadCrumbNews.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@id": `https://www.esyoil.com${item.linkValue}`,
        name: item.name,
      },
    }));
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadCrumbsArray,
      },
    ];
  }
  head() {
    /**
     * Beim laden des Kommentars ging etwas schief. Wahrscheinlich gibt es die ID
     * nicht in der DB oder es wurde keine ID mitgegeben. Dadurch ist singleBlogPost
     * leer und wir haben hier keine Datenn die wior anzeigen können. Also zeigen
     * wir die 404 Seite
     */
    if (!this.singleBlogPost) {
      this.$nuxt.error({
        statusCode: 404,
        message: "Es wurde kein News-Eintrag gefunden.",
      });
      return;
    }

    if (this.singleBlogPost.slug !== this.$route.params.id) {
      this.$nuxt.error({
        statusCode: 404,
        message: "Es wurde kein News-Eintrag gefunden.",
      });
      return;
    }

    const title = this.singleBlogPost.title?.rendered
      ? `${this.singleBlogPost.title?.rendered} (${this.postDate.date.postDate})`
      : "Heizöl-News bei esyoil";
    const description = `${this.singleBlogPost.excerpt?.rendered}`.replace(
      /(<([^>]+)>)/gi,
      "",
    );
    const robots = this.isShortVersion
      ? "noindex,nofollow,noodp"
      : "index,follow,noodp";
    const date = this.singleBlogPost.additionalParameters.date.dd;

    return {
      ...generateMetaTags(this.$route.path, "news", [
        { k: "title", v: title ?? "" },
        { k: "description", v: description ?? "" },
        { k: "robots", v: robots },
        {
          k: "image",
          v: `${process.env.PARTNER_MEDIA_CDN}/img/blogposts/${date}.jpg`,
        },
        { k: "url", v: this.fullPath },
      ]),
      link: [
        {
          rel: "canonical",
          href: this.fullPath,
        },
        {
          rel: "publisher",
          href: "https://plus.google.com/+Esyoil-Heizoelpreise/",
        },
      ],
      bodyAttrs: {
        class: this.isShortVersion ? "shortVersion" : "",
      },
    };
  }

  @BlogModule.State("singleBlogPost")
  singleBlogPost!: any;

  @BlogModule.State("date")
  blogDate!: any;

  @BlogModule.State("crossLinks")
  crossLinks!: any;

  @BreadCrumbModule.Mutation("setNewsBreadCrumbForIndividualBlogPost")
  setNewsBreadCrumbForIndividualBlogPost;

  @BreadCrumbModule.State("breadCrumbNews")
  breadCrumbNews;

  @EsyoilModule.State("userPriceTrendVoting")
  userPriceTrendVoting!: any;

  @EsyoilModule.State("pricePredictionVoting")
  pricePredictionVoting!: any;

  @EsyoilModule.Action("checkUserVotingPermission")
  checkUserVotingPermission;

  @EsyoilModule.Action("fetchUserPriceTrendVoting")
  fetchUserPriceTrendVoting;

  userHasVoted = false;
  isShortVersion = this.$route.query?.shortVersion ?? false;
  isDarkMode = this.$route.query?.mode === "dark" ?? false;

  async fetch({ route, store }: { route: any; store: any }) {
    await Promise.all([
      store.dispatch("blog/fetchSingleBlogPost", route.query.id),
      store.dispatch("blog/fetchAuthorsData"),
    ]);
    store.commit("breadcrumb/setNewsBreadCrumbForIndividualBlogPost", {
      blogPost: store.state.blog.singleBlogPost,
      routePath: route.fullPath,
    });
    await store.dispatch("blog/fetchSingleBlogPostCrossLinks");
  }

  async mounted() {
    window["gtag_enable_tcf_support"] = true;
    if (this.isDarkMode) {
      document.body.style.backgroundColor = "#000";
    }

    this.fetchUserVotingData(this.$route.query.id);
    this.initiallyCheckForVotingPermission(this.$route.query.id);
  }

  checkForValidVotingDate(): boolean {
    if (this.userHasVoted) return false;
    if (
      this.singleBlogPost.additionalParameters.date.postDate ===
      this.blogDate.today
    )
      return true;
    return false;
  }

  initiallyCheckForVotingPermission(articleID) {
    this.checkUserVotingPermission(articleID);
  }

  checkForVotingPermission(): boolean {
    if (this.pricePredictionVoting.userHasVoted === false) return true;
    return false;
  }

  fetchUserVotingData(blogPostID) {
    this.fetchUserPriceTrendVoting(blogPostID);
  }

  getShortVersionNews() {
    const striptags = require("striptags");

    return striptags(this.singleBlogPost.content.rendered, [
      "p",
      "i",
      "icons",
      "img",
      "h1",
      "h2",
      "h3",
      "h4",
    ]);
  }

  getLongerVersionNews() {
    const regex =
      /<a([^>]*)\s+href="([^"]*)"[^>]*\s+target="(_blank|_self)"[^>]*>(.*?)<\/a>/gi;

    const updatedHtmlString = this.singleBlogPost.content.rendered
      .replace(regex, (match, attributes, linkHref, targetValue, linkText) => {
        const relValue =
          targetValue === "_blank" ? "nofollow noopener noreferrer" : "follow";

        attributes = attributes.replace(
          /(\s*rel="[^"]*")?/,
          ' rel="' + relValue + '"',
        );

        return (
          "<a" +
          ' href="' +
          linkHref +
          '" target="' +
          targetValue +
          '" ' +
          attributes +
          ">" +
          linkText +
          "</a>"
        );
      })
      .replaceAll("\\n", "<br>");

    return updatedHtmlString;
  }

  formatCrossLinkDate(dateString: string): string {
    return dayjs(dateString).format("DD.MM.YYYY");
  }

  get postDate() {
    return this.singleBlogPost.additionalParameters;
  }

  get headlineTime() {
    const data = this.singleBlogPost.additionalParameters;
    const time = dayjs(new Date(data.date.iso)).format("HH:mm");

    return `${data.date.dd}.${data.date.mm}.${data.date.yyyy}, ${time}`;
  }

  get fullPath() {
    return `${process.env.MAIN_ESYOIL}${this.$route.fullPath}`;
  }

  get blogpostImageWebp() {
    return `${process.env.PARTNER_MEDIA_CDN}/img/blogposts/${this.singleBlogPost.additionalParameters.date.dd}.webp`;
  }

  get blogpostImageJpg() {
    return `${process.env.PARTNER_MEDIA_CDN}/img/blogposts/${this.singleBlogPost.additionalParameters.date.dd}.jpg`;
  }
}
